import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getCategories, getTours } from '../services/api';
import { Star, X, SlidersHorizontal } from 'lucide-react';
import SEO from './SEO';
import { imgErrorFallback } from '../utils/imageFallback';
import WishlistButton from './WishlistButton';
import SpecialEventCard from './SpecialEventCard';

const formatPrice = (p) => new Intl.NumberFormat('tr-TR').format(p) + ' ₺';

const TourCard = ({ tour }) => (
  <div className="relative">
    <WishlistButton tourId={tour.id} tourTitle={tour.title} />
    <Link
      to={`/tur/${tour.slug}`}
      data-testid={`tour-card-${tour.slug}`}
      className="group block bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={tour.heroImage}
          alt={tour.heroImageAlt || tour.title}
          onError={imgErrorFallback}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {tour.onSale && (
          <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">İndirim</span>
        )}
        {tour.levelName && (
          <span
            data-testid={`tour-level-badge-${tour.slug}`}
            className="absolute bottom-3 right-3 bg-white/95 backdrop-blur text-[#1B1B1B] text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm"
            title={`Seviye ${tour.level}`}
          >
            Sv. {tour.level} · {tour.levelName}
          </span>
        )}
      </div>
    <div className="p-4">
      <h3
        className="text-[15px] font-bold text-[#1B1B1B] mb-1.5 leading-tight line-clamp-2"
        style={{ fontFamily: 'DM Sans, sans-serif' }}
      >
        {tour.title}
      </h3>
      <p className="text-sm text-gray-500 mb-2">
        {tour.nights} gece · {tour.location}
      </p>
      <div className="flex items-center gap-2 mb-2">
        {tour.onSale && tour.originalPrice && (
          <span className="text-sm text-gray-400 line-through">
            {tour.currency}{formatPrice(tour.originalPrice)}
          </span>
        )}
        <span className="text-base font-bold text-[#1B1B1B]">
          {tour.currency}{formatPrice(tour.salePrice)}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 font-medium">
          Seviye {tour.level} · {tour.levelName}
        </span>
        <div className="flex items-center gap-1">
          <Star size={12} fill="#C8D843" stroke="#C8D843" />
          <span className="text-xs font-semibold text-[#1B1B1B]">{tour.rating || 0}</span>
          <span className="text-xs text-gray-400">({tour.reviewCount || 0})</span>
        </div>
      </div>
    </div>
  </Link>
  </div>
);

const FilterGroup = ({ title, items, selected, onToggle, testIdPrefix, counts }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-6">
      <h3
        className="text-xs uppercase tracking-[0.12em] font-bold text-[#3D4A26] mb-3"
        style={{ fontFamily: 'DM Sans, sans-serif' }}
      >
        {title}
      </h3>
      <div className="space-y-2">
        {items.map((cat) => {
          const checked = selected.includes(cat.id);
          const count = counts?.[cat.id] ?? 0;
          return (
            <label
              key={cat.id}
              className="flex items-center gap-2.5 cursor-pointer hover:bg-gray-50 rounded-md px-2 py-1.5 transition-colors"
              data-testid={`${testIdPrefix}-${cat.id}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(cat.id)}
                className="w-4 h-4 accent-[#C8D843] cursor-pointer"
              />
              <span className="text-sm text-[#1B1B1B] flex-1">{cat.name}</span>
              <span className="text-xs text-gray-400" data-testid={`${testIdPrefix}-count-${cat.id}`}>{count}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const ToursPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Selected categoryIds derived from URL
  const selected = useMemo(() => {
    const raw = searchParams.get('category');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);

  useEffect(() => {
    Promise.all([getCategories(), getTours({ limit: 200 })])
      .then(([cRes, tRes]) => {
        setCategories(cRes.data || []);
        setTours(tRes.data || []);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const routes = useMemo(() => categories.filter((c) => c.type === 'route'), [categories]);
  const collections = useMemo(() => categories.filter((c) => c.type === 'collection'), [categories]);

  // Gerçek turlardan kategori sayaçlarını hesapla (backend `count` alanı stale)
  const categoryCounts = useMemo(() => {
    const map = {};
    tours.forEach((t) => {
      (t.categoryIds || []).forEach((id) => {
        map[id] = (map[id] || 0) + 1;
      });
    });
    return map;
  }, [tours]);

  // ── Custom sort order ──
  // 1. Likya Yolu first, Karia Yolu last
  // 2. Within each route: 3 gece tours before 4 gece
  // 3. Within each nights group: Fethiye → Kaş → Gelidonya/Olympos
  const sortKey = (t) => {
    const s = `${t.slug || ''} ${t.location || ''}`.toLowerCase();
    const isKaria = s.includes('karia') || s.includes('datca') || s.includes('datça');
    let city = 50;
    if (s.includes('fethiye')) city = 1;
    else if (s.includes('kas') || s.includes('kaş')) city = 2;
    else if (s.includes('gelidonya') || s.includes('olympos') || s.includes('olimpos')) city = 3;
    else if (s.includes('datca') || s.includes('datça')) city = 1;
    return { route: isKaria ? 2 : 1, nights: t.nights || 99, city };
  };

  const sortTours = (list) =>
    [...list].sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      if (ka.route !== kb.route) return ka.route - kb.route;
      if (ka.nights !== kb.nights) return ka.nights - kb.nights;
      return ka.city - kb.city;
    });

  const toggle = (id) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    if (next.length) setSearchParams({ category: next.join(',') });
    else setSearchParams({});
  };

  const clearAll = () => setSearchParams({});

  const filtered = useMemo(() => {
    const base = selected.length === 0
      ? tours
      : tours.filter((t) => (t.categoryIds || []).some((id) => selected.includes(id)));
    return sortTours(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tours, selected]);

  const selectedNames = categories
    .filter((c) => selected.includes(c.id))
    .map((c) => c.name);

  return (
    <div className="bg-[#fafaf7] min-h-screen">
      <SEO
        title="Tüm Turlar"
        description={`Camption'da ${tours.length}+ rehberli macera turu. Likya Yolu, Karia Yolu ve Türkiye'nin diğer doğa rotalarında 3-4 gecelik trekking deneyimleri. Rota ve gece sayısına göre filtreleyin.`}
      />
      {/* Page header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <h1
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B1B] mb-2"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Tüm Turlar
          </h1>
          <p className="text-gray-600 text-base">
            {filtered.length} tur · doğanın içinde unutulmaz maceralar
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mobile filter trigger */}
        <div className="lg:hidden mb-4 flex items-center justify-between">
          <button
            onClick={() => setMobileFiltersOpen(true)}
            data-testid="open-mobile-filters"
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-sm font-semibold"
          >
            <SlidersHorizontal size={16} /> Filtrele
            {selected.length > 0 && (
              <span className="bg-[#C8D843] text-[#1B1B1B] text-xs font-bold px-1.5 rounded-full">
                {selected.length}
              </span>
            )}
          </button>
          {selected.length > 0 && (
            <button onClick={clearAll} className="text-sm text-gray-500 hover:text-[#1B1B1B]">
              Temizle
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {selectedNames.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {selectedNames.map((name, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-[#1B1B1B] text-xs font-semibold px-3 py-1.5 rounded-full"
              >
                {name}
              </span>
            ))}
            <button
              onClick={clearAll}
              data-testid="clear-filters-button"
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#1B1B1B] px-2"
            >
              <X size={12} /> Tümünü temizle
            </button>
          </div>
        )}

        <div className="flex gap-8">
          {/* Sidebar (desktop) */}
          <aside
            className="hidden lg:block w-[260px] flex-shrink-0"
            data-testid="tours-sidebar"
          >
            <div className="sticky top-24 bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-5">
                <h2
                  className="text-base font-bold text-[#1B1B1B]"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  Filtreler
                </h2>
                {selected.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="text-xs text-gray-500 hover:text-[#1B1B1B] underline"
                  >
                    Temizle
                  </button>
                )}
              </div>
              <FilterGroup
                title="Rotalar"
                items={routes}
                selected={selected}
                onToggle={toggle}
                testIdPrefix="filter-route"
                counts={categoryCounts}
              />
              <FilterGroup
                title="Koleksiyonlar"
                items={collections}
                selected={selected}
                onToggle={toggle}
                testIdPrefix="filter-collection"
                counts={categoryCounts}
              />
            </div>
          </aside>

          {/* Main grid */}
          <main className="flex-1 min-w-0">
            {loading ? (
              <div className="py-20 text-center text-gray-500">Yükleniyor...</div>
            ) : filtered.length === 0 ? (
              <div className="py-20 text-center bg-white rounded-xl border border-gray-100">
                <p className="text-gray-600 mb-2">Bu filtreye uygun tur bulunamadı.</p>
                <button
                  onClick={clearAll}
                  className="text-sm font-semibold text-[#3D4A26] hover:underline"
                >
                  Tüm turları göster
                </button>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
                data-testid="tours-grid"
              >
                <SpecialEventCard />
                {filtered.map((tour) => (
                  <TourCard key={tour.id} tour={tour} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileFiltersOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 lg:hidden"
          onClick={() => setMobileFiltersOpen(false)}
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white p-5 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[#1B1B1B]">Filtreler</h2>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                data-testid="close-mobile-filters"
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            <FilterGroup
              title="Rotalar"
              items={routes}
              selected={selected}
              onToggle={toggle}
              testIdPrefix="filter-mobile-route"
              counts={categoryCounts}
            />
            <FilterGroup
              title="Koleksiyonlar"
              items={collections}
              selected={selected}
              onToggle={toggle}
              testIdPrefix="filter-mobile-collection"
              counts={categoryCounts}
            />
            <button
              onClick={() => setMobileFiltersOpen(false)}
              className="w-full mt-4 bg-[#3D4A26] text-white font-bold py-3 rounded-lg hover:bg-[#2d3a1a] transition-colors"
            >
              {filtered.length} turu göster
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToursPage;
